/**
 * useImageFormatSupport
 *
 * In plain English:
 * - Different browsers support different next‑gen image formats. AVIF is usually smallest,
 *   then WebP, then JPG (widest support).
 * - We quickly test which formats the current browser can decode, then pick the best one.
 * - On the server (SSR), we default to 'jpg' and detect on the client after mount.
 *
 * How it works (step by step):
 * 1) testCanvasSupport: Creates a tiny <canvas>, asks it to export as a given MIME type
 *    (image/avif or image/webp). If the data URL starts with the right MIME, the browser
 *    knows how to encode/decode that format → we treat it as supported.
 * 2) detectPreferredFormat:
 *    - If we're on the server, return 'jpg' (safe default).
 *    - Otherwise, try AVIF; if not supported, try WebP; otherwise fall back to JPG.
 * 3) useEffect: Runs once on mount to set the detected format in state.
 *
 * Media loaders (e.g., Asset) use this to select AVIF → WebP → JPG.
 * Network-aware quality/width tuning happens elsewhere (e.g., based on connection speed).
 */
import { useEffect, useState } from 'react';

type SupportedFormat = 'avif' | 'webp' | 'jpg';

function testCanvasSupport(mimeType: 'image/avif' | 'image/webp'): boolean {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    const dataUrl = canvas.toDataURL(mimeType);
    return dataUrl.startsWith(`data:${mimeType}`);
  } catch {
    return false;
  }
}

function detectPreferredFormat(): SupportedFormat {
  if (typeof window === 'undefined') return 'jpg';
  // Try AVIF via canvas first; if not supported, try WebP; else JPG
  if (testCanvasSupport('image/avif')) return 'avif';
  if (testCanvasSupport('image/webp')) return 'webp';
  return 'jpg';
}

export function useImageFormatSupport(): SupportedFormat {
  const [format, setFormat] = useState<SupportedFormat>('jpg');

  useEffect(() => {
    setFormat(detectPreferredFormat());
  }, []);

  return format;
}
